import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { cn } from "@/lib/cn";
import { springSnappy } from "@/lib/motion";

type Variant = "primary" | "secondary" | "ghost" | "outline" | "danger";
type Size = "sm" | "md" | "lg";

/**
 * Neumorphism 2.0 的按鈕。
 *
 * 關鍵在 .nm-pressable：hover 浮起、按下真的凹進去。
 * primary 用品牌色的柔光而不是灰影，否則彩色按鈕會顯得髒。
 * ghost 平時完全沒有陰影（不搶版面），hover 才浮起來。
 */
const VARIANTS: Record<Variant, string> = {
    /*
   * 紫底白字要過 WCAG AA（4.5:1），紫的明度必須 ≤ 0.47 ——
   * brand-600 (L=0.64) 配白只有 2.8:1，brand-800 (L=0.44) 才有 5.6:1。
   *
   * hover 刻意不換填色：換到淺一階就會掉回 3.6:1。
   * 反饋交給 nm-pressable 的浮起與按壓，觸感更好，也不犧牲可讀性。
   */
  /*
   * 填色由 .nm-brand 用 --nm-fill 畫，不用 bg-* ——
   * .nm-pressable 的 background-image 會蓋掉任何 background-color。
   */
  primary: "nm-pressable nm-brand text-white font-semibold",
  secondary: "nm-pressable text-ink",
  outline: "nm-pressable text-ink",
  ghost: "text-ink-soft hover:text-ink hover:nm-sm",
  // 破壞性操作不靠紅色，靠實心高彩度 + 文案（「永久刪除」）表達
  danger:
    "nm-pressable nm-brand [--nm-fill:var(--color-brand-900)] text-white font-semibold",
};

const SIZES: Record<Size, string> = {
  sm: "h-9 px-4 text-sm gap-1.5",
  md: "h-11 px-6 text-sm gap-2",
  lg: "h-13 px-8 text-base gap-2.5",
};

const BASE =
  "inline-flex items-center justify-center rounded-pill font-medium whitespace-nowrap " +
  "disabled:pointer-events-none disabled:opacity-45 select-none";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** 給 <Link> 用；有 to 就渲染成連結，樣式完全一致 */
  to?: string;
  /** 外部連結 */
  href?: string;
  loading?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
}

/** 帶按壓回饋的按鈕。to / href 會自動切換成連結，避免到處寫 <Link><Button>。 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    to,
    href,
    loading = false,
    icon,
    iconRight,
    fullWidth,
    className,
    children,
    disabled,
    ...props
  },
  ref,
) {
  const classes = cn(BASE, VARIANTS[variant], SIZES[size], fullWidth && "w-full", className);

  const content = (
    <>
      {loading ? <Spinner /> : icon}
      {children}
      {iconRight}
    </>
  );

  if (to) {
    return (
      <motion.span whileTap={{ scale: 0.97 }} transition={springSnappy} className="inline-flex">
        <Link to={to} className={classes}>
          {content}
        </Link>
      </motion.span>
    );
  }

  if (href) {
    return (
      <motion.span whileTap={{ scale: 0.97 }} transition={springSnappy} className="inline-flex">
        <a href={href} target="_blank" rel="noreferrer noopener" className={classes}>
          {content}
        </a>
      </motion.span>
    );
  }

  return (
    <motion.button
      ref={ref}
      whileTap={disabled || loading ? undefined : { scale: 0.97 }}
      transition={springSnappy}
      className={classes}
      disabled={disabled || loading}
      {...(props as React.ComponentProps<typeof motion.button>)}
    >
      {content}
    </motion.button>
  );
});

function Spinner() {
  return (
    <svg className="size-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}
